import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { WebsiteEntity } from './website.entity';

/**
 * Who did what in the website editor (decision D12). Needed because saves and discards
 * change git, not a database row, so the entity audit trail never sees them.
 *
 * Rows are written immediately for actions taken in this Workd environment, and
 * RECONCILED from git by commit SHA for everything else: engineers' merges to the publish
 * branch, and saves made from the other Workd environment (staging and production keep
 * separate tables but share one draft). It never stores content.
 */
export const WEBSITE_ACTIVITY_ACTIONS = [
	'save',
	'discard',
	'draft_sync',
	'publish_requested',
	'revert_requested',
	'link',
	'engineer_change',
	'external_save',
	'external_reset',
] as const;

export type WebsiteActivityAction = (typeof WEBSITE_ACTIVITY_ACTIONS)[number];

// One row per (website, action, commit) — what makes reconcile-from-git idempotent.
@Index('UK_website_activity_commit', ['fkWebsiteId', 'action', 'commitSha'], {
	unique: true,
})
@Index('IDX_website_activity_occurredAt', ['fkWebsiteId', 'occurredAt'], {})
@Entity('website_activity')
export class WebsiteActivityEntity extends BaseEntity {
	@Column('int', { name: 'fk_website__id', unsigned: true })
	public fkWebsiteId!: number;

	// varchar, not ENUM — see the note on WebsitePublishEntity.status.
	@Column('varchar', { name: 'action', length: 32 })
	public action!: WebsiteActivityAction;

	// Null for rows reconciled from git (there is no Workd user behind them here).
	@Column('int', { name: 'fk_user__id_actor', nullable: true, unsigned: true })
	public fkUserIdActor!: number | null;

	// What CUSTOMERS see as the actor: the user's name, or "Workd" for engineers' changes.
	// Never a git author, a service account or a tool name.
	@Column('varchar', { name: 'actorLabel', length: 150 })
	public actorLabel!: string;

	// Workd-staff-only detail: the real git author and a link to the pull request.
	@Column('varchar', { name: 'internalAuthor', nullable: true, length: 150 })
	public internalAuthor!: string | null;

	@Column('varchar', { name: 'internalUrl', nullable: true, length: 500 })
	public internalUrl!: string | null;

	// Null only where no commit exists (linking a website). A unique index treats NULLs as
	// distinct, so several such rows are allowed.
	@Column('varchar', { name: 'commitSha', nullable: true, length: 64 })
	public commitSha!: string | null;

	// 'this' = done in this Workd environment; 'other' = found in git.
	@Column('varchar', { name: 'sourceEnvironment', length: 8, default: 'this' })
	public sourceEnvironment!: 'this' | 'other';

	// Generated from changed field labels, or a pull request title for engineers' changes.
	@Column('varchar', { name: 'summary', length: 1000 })
	public summary!: string;

	@Column('varchar', { name: 'note', nullable: true, length: 500 })
	public note!: string | null;

	// For reconciled rows this is the commit's own time, not when Workd noticed it.
	@Column('datetime', { name: 'occurredAt' })
	public occurredAt!: Date;

	@ManyToOne(() => WebsiteEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_website__id', referencedColumnName: 'id' }])
	public website!: WebsiteEntity;
}
